import {
  Col,
  Input,
  Row,
  Select,
  Typography
} from 'antd';
import React, { memo } from 'react';
import { useDispatch } from 'react-redux';

import DefaultFormItem from 'components/composites/DefaultFormItem';
import AddressFormItems from 'components/forms/AddressFormItems';
import FieldsValue from 'components/forms/FieldsValue';
import FieldValue from 'components/forms/FieldValue';
import FormItemCol from 'components/forms/FormItemCol';
import FormItemEmail from 'components/forms/FormItemEmail';
import { ENTERPRISE_STATUS_LABELS, INDUSTRY_LABELS } from 'constants/enterprise';
import { DEFAULT_COUNTRY_ABBR_FARMBOT, DEFAULT_COUNTRY_ABBR_RANCHBOT } from 'constants/index';
import { ephemeralGetRequest } from 'store/modules/ephemeralRequest/actions';
import { CountryAbbreviation } from 'types/models/country';
import Enterprise, {
  EnterpriseStatus,
  EnterpriseSubdomain,
  Industry
} from 'types/models/enterprise';
import constructEnterpriseDomain from 'utils/construct-enterprise-domain';
import { isRanchbot } from 'utils/is-x-bot';

import { EditEnterpriseOwner } from './EditEnterpriseOwner';
import FormItemTimezone from '../FormItemTimezone';

import './styles.less';

const isDisabledOrDuplicate = (status?: EnterpriseStatus) => {
  return status === EnterpriseStatus.DISABLED || status === EnterpriseStatus.DUPLICATE;
};

interface Props {
  isBackOffice?: boolean;
  /** Use responsive column width */
  isFullSpanOnMobileHalfOnDesktop?: boolean;
  readOnly?: boolean;
  showSubdomainField?: boolean;
  initialValues?: Partial<Enterprise>;
}

function EnterpriseFormItems({
  isBackOffice = false,
  isFullSpanOnMobileHalfOnDesktop,
  readOnly,
  showSubdomainField,
  initialValues
}: Props): JSX.Element | null {
  const dispatch = useDispatch();
  const enterpriseId = initialValues?.id;
  const isNewEnterprise = !enterpriseId;
  const isEnterpriseDisabledOrDuplicate = isDisabledOrDuplicate(initialValues?.status);
  const initialSubdomain: EnterpriseSubdomain | undefined =
    initialValues?.subdomain;

  const defaultCountryAbbreviation: CountryAbbreviation = isRanchbot()
    ? DEFAULT_COUNTRY_ABBR_RANCHBOT
    : DEFAULT_COUNTRY_ABBR_FARMBOT;

  const statusOptions = Object
    .values(EnterpriseStatus)
    .filter((status) => {
      if (isNewEnterprise) {
        return !isDisabledOrDuplicate(status);
      }
      return true;
    })
    .map((status) => ({ value: status, label: ENTERPRISE_STATUS_LABELS[status] }));

  const showEnterpriseOwnerLabel = isBackOffice || !isEnterpriseDisabledOrDuplicate;

  return (
    <Row gutter={24}>
      <FormItemCol
        isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
      >
        <DefaultFormItem
          name="name"
          label="Enterprise Name"
          rules={[
            {
              required: true,
              message: 'Please enter an enterprise name'
            }
          ]}
          readOnly={readOnly}
        >
          <Input />
        </DefaultFormItem>
      </FormItemCol>

      {showSubdomainField && (
        <>
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            <DefaultFormItem
              name="subdomain"
              style={{ marginBottom: '4px' }}
              label="Enterprise URL subdomain"
              hasFeedback
              validateFirst
              normalize={(value) => value?.toLowerCase()}
              rules={[
                {
                  required: true,
                  min: 5,
                  max: 40,
                  message: 'Must be 5-40 characters long.'
                },
                {
                  required: true,
                  pattern: /^[a-z0-9-]*$/,
                  message: 'Only lowercase letters, numbers or hyphens.'
                },
                {
                  pattern: /^[^-].*[^-]$/,
                  message: 'Cannot begin or end with a hyphen.'
                },
                {
                  validator: (_, value) =>
                    new Promise<void>((resolve, reject) => {
                      if (value) {
                        if (value === initialSubdomain) {
                          resolve();
                        } else {
                          // Checks if chosen subdomain (value) is unique.
                          dispatch(
                            ephemeralGetRequest(
                              `enterprise/subdomain/${value}`,
                              resolve,
                              reject
                            )
                          );
                        }
                      } else {
                        reject(new Error('Please enter a subdomain.'));
                      }
                    })
                }
              ]}
              readOnly={readOnly}
            >
              <Input autoComplete="off" />
            </DefaultFormItem>

            <FieldsValue
              names={['subdomain', 'id', 'billingAddress', 'industry']}
            >
              {({
                subdomain,
                id,
                billingAddress,
                industry
              }: Partial<Enterprise>) => {
                const enterpriseDomain = constructEnterpriseDomain({
                  subdomain,
                  id,
                  billingAddress,
                  industry
                });

                return (
                  <div className="EnterpriseFormItems-SubdomainPreview">
                    {subdomain && (
                      <Typography.Text copyable>
                        {enterpriseDomain}
                      </Typography.Text>
                    )}
                  </div>
                );
              }}
            </FieldsValue>
          </FormItemCol>
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            <DefaultFormItem
              name="industry"
              label="Industry"
              rules={[
                {
                  required: true,
                  message: 'Please select an industry'
                }
              ]}
              readOnly={readOnly}
              formatReadOnlyValue={(value: Industry) => INDUSTRY_LABELS[value]}
            >
              <Select
                options={Object.values(Industry).map(industry => ({
                  value: industry,
                  label: INDUSTRY_LABELS[industry]
                }))}
              />
            </DefaultFormItem>
          </FormItemCol>
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            <DefaultFormItem
              name="status"
              label="Status"
              rules={[
                {
                  required: true,
                  message: 'Please select an status'
                }
              ]}
              readOnly={readOnly}
              formatReadOnlyValue={(value: EnterpriseStatus) => ENTERPRISE_STATUS_LABELS[value]}
            >
              <Select options={statusOptions} />
            </DefaultFormItem>
          </FormItemCol>
          <FormItemCol
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
          >
            <DefaultFormItem
              name="note"
              label="Farmbot Internal Notes"
              readOnly={readOnly}
            >
              <Input.TextArea maxLength={2000} />
            </DefaultFormItem>
          </FormItemCol>
        </>
      )}

      {/* Section: ENTERPRISE OWNER */}
      {showEnterpriseOwnerLabel && (
        <Col span={24}>
          <Typography.Title level={5}>Enterprise Owner</Typography.Title>
        </Col>
      )}
      {isNewEnterprise
        ? <Col span={24}><FormItemEmail /></Col>
        : (
          <FieldValue name="status">
            {(status: EnterpriseStatus) => (
              <EditEnterpriseOwner
                enterpriseId={enterpriseId}
                initialOwner={initialValues?.owner}
                status={status}
                readOnly={readOnly}
                isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
                isBackOffice={isBackOffice}
              />
            )}
          </FieldValue>
        )
      }

      <Col span={24}>
        <Typography.Title level={5}>Billing Address</Typography.Title>
      </Col>
      <FieldValue name={['billingAddress', 'country']}>
        {(countryAbbreviation) => (
          <AddressFormItems
            topLevelName="billingAddress"
            isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
            readOnly={readOnly}
            hasInitialValues={!!initialValues?.billingAddress?.country}
            countryAbbreviation={countryAbbreviation as string}
          />
        )}
      </FieldValue>
      {isNewEnterprise && (
        <FormItemCol
          isFullSpanOnMobileHalfOnDesktop={isFullSpanOnMobileHalfOnDesktop}
        >
          <FieldValue name={['billingAddress', 'country']}>
            {(countryAbbreviation) => (
              <FormItemTimezone
                formItemName="timezoneCode"
                readOnly={readOnly}
                countryAbbreviation={
                  (countryAbbreviation as CountryAbbreviation) ||
                  defaultCountryAbbreviation
                }
              />
            )}
          </FieldValue>
        </FormItemCol>
      )}
    </Row>
  );
}

export default memo(EnterpriseFormItems);
